// - - - - - - - - - -
// #193 — "Book multiple days" recommendation for over-capacity assignments.
//
// Brandon's spec (issue #193):
//   "When a once/day job is assigned that is greater than the installer's
//    current once/day square foot capacity, present a pop up recommending to
//    book the job for multiple days (total square foot of job divided by
//    once/day capacity = # of days recommended for job)."
//
// This module is the PURE math behind that advisory. It is read-only: it does
// NOT change the assignment, the date range, or any mutation payload. The
// caller (JobInitModal, the drag-drop assign popup) renders an advisory banner
// when `exceedsCapacity` is true; the install manager decides whether to act
// on it (e.g. by extending the Est-Complete date to span the recommended days).
//
// UNIT = square feet, end-to-end, matching Brandon's wording:
//   • Job size per surface TYPE:
//       - Carpet:       sum of Materials[type=Carpet] `total_sqft`.
//       - Hard surface: sum of the type's MAIN-SKU InvoiceLineItems `qty`
//                       (a matched main-SKU line item's qty IS its sqft — the
//                       exact mechanism the hard-surface capacity engine uses;
//                       see hardSurfaceCapacityRemaining.js header).
//   • Installer once/day capacity per type: `capacity_<field>_once` — already
//     stored in square feet on the Candidate doc. NO ÷9 conversion.
//
// Classifying by SKU (hard) + Materials (carpet) deliberately AVOIDS the
// ambiguous display-name → capacity-field mapping (Plank/Wood/Vinyl) that the
// original capacity design note flagged as needing a business decision. A job
// can span more than one type; we evaluate each independently and recommend
// based on the BINDING type (the one needing the most days).

import { sumLineItemQtyForSkuList } from "./installerCapacityRemaining";
import { HARD_SURFACE_CAPACITY_TYPES, MILLISECONDS_PER_DAY } from "./keys";

const num = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};

/**
 * #193 — given the job's scheduled (start) date and the recommended number of
 * days, return the Estimated-Complete date that spans that many days. "N days"
 * is inclusive of the start day, so the latest date = start + (N − 1) days.
 *
 * Calendar days (matches JobInitModal's existing Est-Complete math, which adds
 * `MILLISECONDS_PER_DAY * dayDifference`). NOTE: this does NOT skip weekends —
 * see the #193 open question for Brandon on business-day vs calendar-day spans.
 *
 * Pure + exported because the returned value flows into the
 * `date_estimatedCompletion` mutation payload (→ FileMaker), so it must be
 * test-pinned per the project's change-detection / FileMaker-payload rules.
 *
 * @param {string} dateScheduled    — "M/D/YYYY" start date
 * @param {number} recommendedDays  — integer ≥ 1
 * @returns {string} the en-us locale date string, or "" if inputs are invalid.
 */
export function computeRecommendedEstComplete(dateScheduled, recommendedDays) {
  if (!dateScheduled) return "";
  const days = Math.floor(Number(recommendedDays));
  if (isNaN(days) || days < 1) return "";
  const startMs = Date.parse(dateScheduled);
  if (isNaN(startMs)) return "";
  return new Date(
    startMs + MILLISECONDS_PER_DAY * (days - 1)
  ).toLocaleDateString("en-us");
}

/**
 * Square footage of the Carpet portion of a job, from its Materials rows.
 * Mirrors getCarpetYardsForJob's field-name convention but reads total_sqft.
 * Defensive against missing Materials / keys / NaN / negative values.
 */
export function getCarpetSqftForJob(doc) {
  if (!doc || !Array.isArray(doc.Materials)) return 0;
  let total = 0;
  for (const material of doc.Materials) {
    if (!material) continue;
    if (material["KI_AQ » Materials::type"] !== "Carpet") continue;
    const sqft = parseFloat(material["KI_AQ » Materials::total_sqft"]);
    if (!isNaN(sqft) && sqft > 0) total += sqft;
  }
  return total;
}

/**
 * The job's size (sqft) broken down by the surface TYPES it actually contains.
 * Returns [{ type, capacityField, jobSqft }] — Carpet first (if present), then
 * any hard-surface type whose main-SKU line items the job matches. Types with
 * zero size are omitted.
 *
 * `type` is the human label ("Carpet", "Tile", "VinylPlank", …);
 * `capacityField` is the Candidate capacity-field stem ("carpet", "tile",
 * "vinyl", …) used to read `capacity_<field>_once`.
 */
export function getJobSizesByType(doc) {
  const out = [];
  if (!doc) return out;
  const carpetSqft = getCarpetSqftForJob(doc);
  if (carpetSqft > 0) {
    out.push({ type: "Carpet", capacityField: "carpet", jobSqft: carpetSqft });
  }
  for (const t of HARD_SURFACE_CAPACITY_TYPES) {
    const sqft = sumLineItemQtyForSkuList(doc, t.mainSkus);
    if (sqft > 0) {
      out.push({ type: t.type, capacityField: t.capacityField, jobSqft: sqft });
    }
  }
  return out;
}

/**
 * Compute the multi-day "book it across N days" recommendation for assigning
 * `job` to `candidate`.
 *
 * For each surface type the job contains, compares the job's sqft against the
 * installer's once/day capacity for that type. A type only participates when
 * the installer has a configured (> 0) once/day capacity for it — we can't
 * advise on a type the installer isn't set up for.
 *
 * @param {Object} params
 * @param {Object} params.job        — the ActiveQueue doc being assigned
 * @param {Object} params.candidate  — the installer Candidate doc
 * @returns {{
 *   exceedsCapacity: boolean,     // true → show the advisory
 *   recommendedDays: number,      // ceil(jobSqft / onceCap) for the binding type (1 if none exceed)
 *   bindingType: Object|null,     // the per-type entry that drives recommendedDays
 *   perType: Array<{ type, capacityField, jobSqft, onceCap, exceeds, recommendedDays }>,
 * }}
 */
export function computeMultiDayJobRecommendation({ job, candidate }) {
  const none = {
    exceedsCapacity: false,
    recommendedDays: 1,
    bindingType: null,
    perType: [],
  };
  if (!job || !candidate) return none;

  const sizes = getJobSizesByType(job);
  if (sizes.length === 0) return none;

  const perType = [];
  let binding = null;
  for (const s of sizes) {
    const onceCap = num(candidate[`capacity_${s.capacityField}_once`]);
    // Installer not configured for this type → no basis to advise. Skip.
    if (onceCap <= 0) continue;
    const exceeds = s.jobSqft > onceCap;
    const recommendedDays = exceeds ? Math.ceil(s.jobSqft / onceCap) : 1;
    const entry = {
      type: s.type,
      capacityField: s.capacityField,
      jobSqft: s.jobSqft,
      onceCap,
      exceeds,
      recommendedDays,
    };
    perType.push(entry);
    if (exceeds && (!binding || recommendedDays > binding.recommendedDays)) {
      binding = entry;
    }
  }

  if (!binding) return { ...none, perType };
  return {
    exceedsCapacity: true,
    recommendedDays: binding.recommendedDays,
    bindingType: binding,
    perType,
  };
}
